import { cn } from "@/lib/utils";

const spinnerSizes = {
  sm: "h-4 w-4 border-2",
  md: "h-8 w-8 border-[3px]",
  lg: "h-12 w-12 border-4",
} as const;

export type SpinnerSize = keyof typeof spinnerSizes;

export interface SpinnerProps {
  /** Default "md". */
  size?: SpinnerSize;
  /** Screen reader text. `null` makes it decorative (e.g. inside a busy button). */
  label?: string | null;
  className?: string;
}

/** Gold ring spinner. */
export function Spinner({ size = "md", label = "Loading", className }: SpinnerProps) {
  const ring = (
    <span
      aria-hidden="true"
      className={cn(
        "ds-animate-spin inline-block shrink-0 rounded-full border-ds-gold-primary/25 border-t-ds-gold-primary",
        spinnerSizes[size],
        label === null && className,
      )}
    />
  );
  if (label === null) return ring;
  return (
    <span role="status" className={cn("inline-flex", className)}>
      {ring}
      <span className="sr-only">{label}</span>
    </span>
  );
}

export interface LoadingProps {
  /** Visible message under the spinner. Default "Loading…". */
  message?: string;
  size?: SpinnerSize;
  /** Cover the viewport with a dimmed overlay. */
  fullscreen?: boolean;
  className?: string;
}

/** Centered spinner with a message, for sections or whole pages that are loading. */
export function Loading({
  message = "Loading…",
  size = "lg",
  fullscreen = false,
  className,
}: LoadingProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center gap-4 py-12 text-center",
        fullscreen && "fixed inset-0 z-ds-modal bg-ds-black-primary/80 backdrop-blur-sm",
        className,
      )}
    >
      <Spinner size={size} label={null} />
      <p className="font-heading text-[length:var(--font-size-sm)] uppercase tracking-[0.25em] text-ds-gold-light">
        {message}
      </p>
    </div>
  );
}
